import { useEffect, useRef } from "react";
import Chart from "chart.js/auto";

const TeamWorkload = ({ selectedTeam, teamsData, teamMembersData }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const existingChart = Chart.getChart(canvasRef.current);
    if (existingChart) {
      existingChart.destroy();
    }

    // If "All" is selected, show teams chart
    if (selectedTeam === "All" || !selectedTeam) {
      const displayData = teamsData || [];
      if (!displayData || displayData.length === 0) return;

      const maxTasks = Math.max(...displayData.map((d) => d.tasks || 0), 0) || 20;

      chartRef.current = new Chart(canvasRef.current, {
        type: "bar",
        data: {
          labels: displayData.map((d) => d?.teamName ?? d?.name ?? ""),
          datasets: [
            {
              label: "Tasks",
              data: displayData.map((d) => d?.tasks || 0),
              backgroundColor: displayData.map((d) => {
                if (d?.color === "bg-red-500") return "#ef4444";
                if (d?.color === "bg-orange-500") return "#f97316";
                if (d?.color === "bg-green-500") return "#22c55e";
                return "#3b82f6";
              }),
              borderRadius: 4,
              barPercentage: 0.6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context) => {
                  return `Tasks: ${context.parsed.y}`;
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
            },
            y: {
              beginAtZero: true,
              max: Math.ceil(maxTasks * 1.2),
              grid: { drawBorder: false },
              ticks: {
                stepSize: 1,
                callback: function (value) {
                  if (Number.isInteger(value)) return value;
                  return null;
                },
              },
            },
          },
        },
      });
    } else {
      // Show team members chart when a specific team is selected
      const members = (teamMembersData && teamMembersData[selectedTeam]) || [];
      if (!members || members.length === 0) return;

      const maxTasks = Math.max(...members.map((m) => m.tasks || 0), 0) || 20;

      chartRef.current = new Chart(canvasRef.current, {
        type: "bar",
        data: {
          labels: members.map((m) => m?.name || ""),
          datasets: [
            {
              label: "Tasks",
              data: members.map((m) => m?.tasks || 0),
              backgroundColor: "#3b82f6",
              borderRadius: 4,
              barPercentage: 0.6,
            },
            {
              label: "Projects",
              data: members.map((m) => m?.projects || 0),
              backgroundColor: "#22c55e",
              borderRadius: 4,
              barPercentage: 0.6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: "top",
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context) => {
                  return `${context.dataset.label}: ${context.parsed.y}`;
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
            },
            y: {
              type: "linear",
              display: true,
              beginAtZero: true,
              max: Math.ceil(maxTasks * 1.2),
              grid: { drawBorder: false },
              ticks: {
                stepSize: 1,
                callback: function (value) {
                  if (Number.isInteger(value)) return value;
                  return null;
                },
              },
              title: {
                display: true,
                text: "Count",
              },
            },
          },
        },
      });
    }

    return () => chartRef.current?.destroy();
  }, [selectedTeam, teamsData, teamMembersData]);

  return (
    <div style={{ width: "100%", height: 300 }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

export default TeamWorkload;
